import React from 'react';
import { UploadOutlined } from '@ant-design/icons';
import { Button, Collapse, Form, Select, Upload } from 'antd';
import { MaterialDetailsCollapseProps } from '../../interfaces';
import { normalizeUploadEvent } from '../adminProductForm.utils';
import { rejectInvalidAnimationUpload, rejectInvalidImageUpload } from '../../../utils/uploadValidation';
import MaterialVariantStockFields from './MaterialVariantStockFields';

const MaterialDetailsCollapse: React.FC<MaterialDetailsCollapseProps> = ({
  field,
  forceOpen = false,
  materialIndex,
  allowedSizeIds,
  filteredSizes,
  requiresSizes,
}) => (
  <Collapse
    activeKey={forceOpen ? ['details'] : undefined}
    className="admin-product-form__material-details-collapse"
    items={[{
      key: 'details',
      label: 'Open material details',
      children: (
        <>
          <Form.Item
            name={[field.name, 'images']}
            label="Material Images"
            valuePropName="fileList"
            getValueFromEvent={normalizeUploadEvent}
            className="admin-product-form__material-upload"
            rules={[{
              validator: async (_, value = []) =>
                value.length
                  ? Promise.resolve()
                  : Promise.reject(new Error('Upload at least one image for this material.')),
            }]}
          >
            <Upload multiple listType="picture" beforeUpload={rejectInvalidImageUpload} accept="image/jpeg,image/png,image/webp">
              <Button icon={<UploadOutlined />}>Upload Images</Button>
            </Upload>
          </Form.Item>
          <Form.Item
            name={[field.name, 'animation']}
            label="Material Animation"
            valuePropName="fileList"
            getValueFromEvent={normalizeUploadEvent}
            className="admin-product-form__material-upload"
          >
            <Upload
              maxCount={1}
              listType="text"
              beforeUpload={rejectInvalidAnimationUpload}
              accept="video/mp4,video/quicktime,video/webm,.mov,.mp4,.webm"
            >
              <Button icon={<UploadOutlined />}>Upload Animation</Button>
            </Upload>
          </Form.Item>
          <Form.Item
            name={[field.name, 'sizeIds']}
            label="Available Sizes For This Material"
            className="admin-product-form__material-sizes"
            rules={[{
              validator: async (_, value: string[] = []) => {
                if (requiresSizes && !value.length) {
                  return Promise.reject(new Error('Select at least one size for this material.'));
                }

                if (!requiresSizes && value.length) {
                  return Promise.reject(new Error('This product type does not use selectable sizes.'));
                }

                const invalidSizeIds = value.filter((id) => !allowedSizeIds.includes(id));
                return invalidSizeIds.length
                  ? Promise.reject(new Error('Selected sizes do not match product type and gender.'))
                  : Promise.resolve();
              },
            }]}
          >
            <Select
              mode="multiple"
              allowClear
              disabled={!requiresSizes || !filteredSizes.length}
              placeholder={
                requiresSizes
                  ? filteredSizes.length ? 'Select sizes for this material' : 'No sizes for selected type/gender'
                  : 'No size required for this product type'
              }
              options={filteredSizes.map((size) => ({
                value: size.id,
                label: `${size.label} (${size.value})`,
              }))}
            />
          </Form.Item>
          <MaterialVariantStockFields
            materialIndex={materialIndex}
            filteredSizes={filteredSizes}
            requiresSizes={requiresSizes}
          />
        </>
      ),
    }]}
  />
);

export default MaterialDetailsCollapse;
